(() => {
  if (customElements.get('marame-cart-discount')) return;

  const elements = new Set();
  const state = { open: false, input: '', message: '', code: '', busy: false, saving: null };
  let feedbackTimer;
  let queuedCode = '';
  let cartRevision = 0;
  const normalize = (code) => typeof code === 'string' ? code.trim() : '';
  const sameCode = (a, b) => normalize(a).toUpperCase() === normalize(b).toUpperCase();
  const uniqueCodes = (values) => values.reduce((codes, value) => {
    const code = normalize(value);
    if (code && !code.includes(',') && !codes.some((existing) => sameCode(existing, code))) codes.push(code);
    return codes;
  }, []);
  const applications = (cart) => [
    ...(Array.isArray(cart?.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
    ...(Array.isArray(cart?.items) ? cart.items.flatMap((item) => (Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : []).map((allocation) => ({ ...allocation.discount_application, total_allocated_amount: allocation.amount }))) : [])
  ].filter(Boolean);
  const existingCodes = (cart) => uniqueCodes([
    ...(Array.isArray(cart?.discount_codes) ? cart.discount_codes.map((discount) => discount.code) : []),
    ...applications(cart).filter((discount) => discount.type === 'discount_code').map((discount) => discount.title)
  ]);
  const outcome = (cart, code) => {
    const entry = cart?.discount_codes?.find((discount) => sameCode(discount.code, code));
    if (entry?.applicable === false) return 'ineligible';
    if (applications(cart).some((discount) => discount.type === 'discount_code' && sameCode(discount.title, code) && Number(discount.total_allocated_amount) > 0)) return 'applied';
    if (entry?.applicable === true) return 'checkout_confirmation';
    return 'unconfirmed';
  };
  const cartFingerprint = (cart) => {
    if (!/^[A-Z]{3}$/.test(cart?.currency) || !Array.isArray(cart?.items) || !cart.items.length || cart.items.some((item) => typeof item?.key !== 'string' || !item.key || !Number.isSafeInteger(item.quantity) || item.quantity <= 0)) return null;
    return JSON.stringify([cart.currency, cart.items.map((item) => [item.key, item.quantity]).sort((a, b) => a[0].localeCompare(b[0]) || a[1] - b[1])]);
  };
  const appliedSaving = (cart, code) => {
    const matching = applications(cart).filter((discount) => discount.type === 'discount_code' && sameCode(discount.title, code) && Number(discount.total_allocated_amount) > 0);
    const numericValue = (value) => typeof value === 'number' || (typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value)) ? Number(value) : NaN;
    const validAllocations = matching.length > 0 && matching.every((discount) => Number.isSafeInteger(numericValue(discount.total_allocated_amount)));
    const percentage = numericValue(matching[0]?.value);
    const hasPercentage = validAllocations && Number.isFinite(percentage) && percentage > 0 && percentage <= 100 && matching.every((discount) => discount.value_type === 'percentage' && numericValue(discount.value) === percentage);
    const amount = validAllocations ? matching.reduce((sum, discount) => sum + Number(discount.total_allocated_amount), 0) : 0;
    return { percentage: hasPercentage ? percentage : null, amount, currency: cart?.currency };
  };
  const appliedMessage = (labels) => {
    const saving = state.saving;
    const locale = document.documentElement?.lang || 'en';
    try {
      if (saving?.percentage && labels.applied_percentage) {
        const percentage = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 8 }).format(saving.percentage / 100);
        return labels.applied_percentage.replace('__percentage__', percentage);
      }
      if (Number.isSafeInteger(saving?.amount) && saving.amount > 0 && /^[A-Z]{3}$/.test(saving.currency) && labels.applied_amount) {
        const amount = new Intl.NumberFormat(locale, { style: 'currency', currency: saving.currency, currencyDisplay: saving.currency === 'GBP' ? 'symbol' : 'code', ...(saving.amount % 100 === 0 ? { minimumFractionDigits: 0 } : {}) }).format(saving.amount / 100);
        return labels.applied_amount.replace('__amount__', amount);
      }
    } catch {}
    return labels.applied || '';
  };
  const clearFeedback = () => {
    clearTimeout(feedbackTimer);
    feedbackTimer = undefined;
    state.saving = null;
  };
  const removalOutcome = (cart, code) => existingCodes(cart).some((existing) => sameCode(existing, code)) ? 'remove_error' : 'removed';
  const invalidateCosts = () => document.querySelectorAll('marame-cart-costs').forEach((element) => {
    if (element.ready && typeof element.invalidate === 'function') element.invalidate(true);
  });
  const render = () => elements.forEach((element) => element.renderState());
  const reportResult = (code, status, removing) => {
    document.dispatchEvent(new CustomEvent('marame:discount-result', { detail: { code, status, action: removing ? 'remove' : 'apply' } }));
    return { status, code };
  };
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) throw new Error('cart-discount-request');
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price)) throw new Error('cart-discount-response');
      return cart;
    } finally { clearTimeout(timeout); }
  };

  class MarameCartDiscount extends HTMLElement {
    static existingCodes = existingCodes;
    static outcome = outcome;

    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('[data-discount-form]');
      this.input = this.querySelector('[data-discount-input]');
      this.toggle = this.querySelector('[data-discount-toggle]');
      this.status = this.querySelector('[data-discount-status]');
      this.submit = this.querySelector('[data-discount-submit]');
      this.drawer = this.closest('cart-drawer');
      this.confirmation = null;
      try {
        const confirmation = JSON.parse(this.querySelector('script[data-discount-confirmation]')?.textContent || 'null');
        if (cartFingerprint(confirmation) && Number.isSafeInteger(confirmation.total_price) && Array.isArray(confirmation.cart_level_discount_applications)) this.confirmation = confirmation;
      } catch {}
      if (!this.form || !this.input || !this.toggle || !this.status || !this.submit || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.labels = this.dataset;
      this.onToggle = () => { state.open = !state.open; render(); if (state.open) this.input.focus(); };
      this.onInput = () => { state.input = this.input.value; };
      this.onSubmit = (event) => { event.preventDefault(); event.stopPropagation(); this.applyCode(this.input.value, true); };
      this.onRemove = (event) => {
        const button = event.target.closest?.('[data-discount-remove]');
        if (button && this.contains(button)) { event.preventDefault(); this.removeCode(button.dataset.discountRemove, true); }
      };
      this.toggle.addEventListener('click', this.onToggle);
      this.input.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onRemove);
      elements.add(this);
      this.ready = true;
      this.hidden = false;
      this.renderState();
      if (queuedCode && !state.busy) { const code = queuedCode; queuedCode = ''; this.applyCode(code); }
    }

    disconnectedCallback() {
      elements.delete(this);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.input?.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onRemove);
      this.ready = false;
    }

    renderState() {
      this.form.hidden = !state.open;
      this.toggle.setAttribute('aria-expanded', String(state.open));
      this.setAttribute('aria-busy', String(state.busy));
      this.input.value = state.input;
      this.input.disabled = state.busy;
      this.submit.disabled = state.busy;
      this.toggle.disabled = state.busy;
      document.querySelectorAll('[data-discount-remove]').forEach((button) => { button.disabled = state.busy; });
      this.submit.textContent = state.busy ? this.labels.loading : this.labels.apply;
      this.status.textContent = state.message === 'applied' ? appliedMessage(this.labels) : (this.labels[state.message] || '').replace('__code__', state.code);
      this.status.hidden = !state.message;
      this.status.dataset.state = state.message;
      this.input.setAttribute('aria-invalid', String(['ineligible', 'invalid_input'].includes(state.message)));
    }

    applyCode(value, focus = false) { return this.updateCode(value, focus, false); }

    removeCode(value, focus = false) { return this.updateCode(value, focus, true); }

    async updateCode(value, focus, removing) {
      if (state.busy) return { status: 'busy' };
      clearFeedback();
      const code = normalize(value);
      state.code = code;
      if (!removing) { state.input = code; state.open = true; }
      if (!code || code.includes(',') || code.length > 255 || /[\x00-\x1f\x7f]/.test(code)) {
        state.message = 'invalid_input'; render(); return reportResult(code, state.message, removing);
      }
      const root = window.Shopify?.routes?.root;
      if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//')) {
        state.message = 'network_error'; render(); return reportResult(code, state.message, removing);
      }
      const prefix = root.endsWith('/') ? root : `${root}/`;
      state.busy = true;
      const revision = cartRevision;
      state.message = removing ? 'removing' : 'loading';
      render();
      reportResult(code, state.message, removing);
      invalidateCosts();
      let cart;
      let submitted = false;
      let message = 'network_error';
      try {
        const before = await request(`${prefix}cart.js`);
        const codes = removing ? existingCodes(before).filter((existing) => !sameCode(existing, code)) : uniqueCodes([...existingCodes(before), code]);
        submitted = true;
        cart = await request(`${prefix}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') })
        });
        message = removing ? removalOutcome(cart, code) : outcome(cart, code);
      } catch {
        if (submitted) {
          try {
            cart = await request(`${prefix}cart.js`);
            const confirmed = removing ? removalOutcome(cart, code) : outcome(cart, code);
            message = ['applied', 'ineligible', 'checkout_confirmation', 'removed'].includes(confirmed) ? confirmed : 'network_error';
          } catch { message = 'network_error'; }
        }
      }
      let savingCart = cart;
      try {
        if (cart) {
          invalidateCosts();
          const previousElements = new Set(elements);
          await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'marame-cart-discount', cartData: cart });
          if (this.isConnected && this.drawer && cart.item_count !== 0) message = 'refresh_error';
          else if (!removing && cartRevision === revision && ['checkout_confirmation', 'unconfirmed'].includes(message)) {
            const fresh = [...elements].find((element) => element !== this && !previousElements.has(element) && element.isConnected && element.ready && element.drawer === this.drawer);
            const fingerprint = cartFingerprint(cart);
            if (fingerprint && fingerprint === cartFingerprint(fresh?.confirmation) && outcome(fresh.confirmation, code) === 'applied') {
              savingCart = fresh.confirmation;
              message = 'applied';
            }
          }
        }
      } catch { message = 'refresh_error'; }
      finally {
        if (cartRevision !== revision && ['applied', 'checkout_confirmation'].includes(message)) message = 'unconfirmed';
        state.busy = false;
        state.message = message;
        state.saving = message === 'applied' ? appliedSaving(savingCart, code) : null;
        if (['applied', 'removed'].includes(message)) {
          feedbackTimer = setTimeout(() => {
            feedbackTimer = undefined;
            state.message = '';
            state.saving = null;
            render();
          }, message === 'removed' ? 2500 : 4000);
        }
        if (['applied', 'checkout_confirmation'].includes(message)) { state.input = ''; state.open = false; }
        render();
        reportResult(code, message, removing);
        const current = [...elements][0];
        if (focus && current && (!current.drawer || current.drawer.classList.contains('active'))) {
          (state.open ? current.input : current.toggle).focus();
        }
        if (queuedCode && current) { const next = queuedCode; queuedCode = ''; current.applyCode(next); }
      }
      return { status: message, code };
    }
  }

  customElements.define('marame-cart-discount', MarameCartDiscount);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'marame-cart-discount') return;
      cartRevision += 1;
      if (state.busy) return;
      clearFeedback();
      state.message = '';
      state.code = '';
      render();
    });
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-discount-remove]');
    if (!button || button.disabled || button.closest('marame-cart-discount')) return;
    const drawer = button.closest('cart-drawer');
    const component = [...elements].find((element) => element.isConnected && element.ready && element.drawer === drawer);
    if (!component) return;
    event.preventDefault();
    component.removeCode(button.dataset.discountRemove, true);
  });
  document.addEventListener('submit', (event) => {
    if (state.busy && (event.target.id === 'CartDrawer-Form' || event.submitter?.name === 'checkout')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('marame:discount-issued', (event) => {
    const code = normalize(event.detail?.code);
    if (!code) return;
    const component = [...elements][0];
    if (state.busy || !component) queuedCode = code;
    else component.applyCode(code);
  });
})();

